import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { isDirty, submit } from 'redux-form';
import { useI18n } from 'hooks/use_i18n';
import Collapse from 'components/ui/collapse';
import { CardHeader, Button, Card, CardBody } from 'components/ui';
import confirm from 'services/confirm';

const ShowEditPanel = ({
  canSave,
  title,
  edit,
  editable,
  show,
  onSave,
  onCancel,
  onEdit,
  editButtonLabel,
  saveButtonLabel,
  cancelButtonLabel,
  editFormName,
  editOnly,
  editActions,
  mode,
  collapse,
  collapsable,
  className,
  ...rest
}) => {
  const [editMode, setEditMode] = useState(editOnly || mode === 'edit');
  const [isCollapsed, setIsCollapsed] = useState(collapse);
  const { t, t_html } = useI18n();
  const dispatch = useDispatch();
  let isEditDirty = false;

  const onSaveClick = async () => {
    if (onSave) {
      await onSave();
    } else {
      dispatch(submit(editFormName));
    }
  };

  const cancel = () => {
    setEditMode(!editMode);
    if (onCancel) {
      onCancel();
    }
  };

  if (editFormName) {
    isEditDirty = useSelector((state) => {
      return isDirty(editFormName)(state);
    });
  }

  const editComponent = React.Children.map(edit, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        onSubmitSuccess: (...params) => {
          if (child.props.onSubmitSuccess) {
            child.props.onSubmitSuccess(...params);
          }
          if (!editOnly) {
            setEditMode(false);
          }
        },
      });
    }
    return child;
  });

  const showComponent = React.Children.map(show, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {});
    }
    return child;
  });

  useEffect(() => {
    setEditMode(editOnly || mode === 'edit');
  }, [mode]);

  return (
    <Card className={className} {...rest}>
      <CardHeader
        title={title}
        beforeTitle={
          collapsable && (
            <Button
              className="mr-1"
              size="sm"
              color="light"
              iconClassName={isCollapsed ? 'icon-rotate-270' : ''}
              onClick={() => {
                return setIsCollapsed(!isCollapsed);
              }}
              icon="arrow-down"
            />
          )
        }
      >
        {editMode && (
          <React.Fragment>
            {editActions && <span className="mr-2">{editActions}</span>}
            {!editOnly && (
              <Button
                color="light"
                className="mr-2"
                data-cy="cancel_button"
                size="sm"
                onClick={() => {
                  if (isEditDirty) {
                    return confirm({
                      title: t('components.ui.show_edit_panel.confirm_cancel_title'),
                      message: t_html('components.ui.show_edit_panel.confirm_cancel_message'),
                      cancelButtonText: t('components.ui.show_edit_panel.cancel_button_text'),
                      proceedButtonText: t('components.ui.show_edit_panel.proceed_button_text'),
                      proceedButtonColor: 'danger',
                      cancelButtonColor: 'default',
                    }).then(cancel);
                  }
                  return cancel();
                }}
              >
                {cancelButtonLabel || t('components.ui.show_edit_panel.cancel_edit_button_text')}
              </Button>
            )}
            {canSave && (
              <Button
                color="success"
                size="sm"
                data-cy="save_button"
                onClick={(e) => {
                  e.preventDefault();
                  return onSaveClick();
                }}
              >
                {saveButtonLabel || t('components.ui.show_edit_panel.save_button_text')}
              </Button>
            )}
          </React.Fragment>
        )}
        {!editMode && editable && (
          <Button
            color="default"
            icon="edit"
            data-cy="edit_button"
            size="sm"
            onClick={() => {
              if (isCollapsed) {
                setIsCollapsed(false);
              }
              setEditMode(!editMode);
              if (onEdit) {
                onEdit();
              }
            }}
          >
            {editButtonLabel || t('components.ui.show_edit_panel.edit_button_text')}
          </Button>
        )}
      </CardHeader>
      <Collapse isOpen={!isCollapsed}>
        <CardBody>
          {editMode && editComponent}
          {!editMode && showComponent}
        </CardBody>
      </Collapse>
    </Card>
  );
};

ShowEditPanel.defaultProps = {
  editButtonLabel: null,
  saveButtonLabel: null,
  cancelButtonLabel: null,
  onCancel: null,
  onEdit: null,
  editFormName: null,
  mode: 'show',
  editOnly: false,
  collapse: false,
  collapsable: false,
  editable: true,
  editActions: null,
  onSave: null,
  canSave: true,
  className: null,
};

ShowEditPanel.propTypes = {
  editButtonLabel: PropTypes.string,
  saveButtonLabel: PropTypes.string,
  cancelButtonLabel: PropTypes.string,
  editFormName: PropTypes.string,
  edit: PropTypes.any.isRequired,
  editActions: PropTypes.any,
  show: PropTypes.any.isRequired,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  onEdit: PropTypes.func,
  title: PropTypes.any.isRequired,
  mode: PropTypes.oneOf(['edit', 'show']),
  editOnly: PropTypes.bool,
  collapse: PropTypes.bool,
  canSave: PropTypes.bool,
  collapsable: PropTypes.bool,
  editable: PropTypes.bool,
  className: PropTypes.string,
};

export default ShowEditPanel;
